package it.neckar.lizergy.model.energy.economics

import it.neckar.lizergy.model.energy.AmountOfEnergy
import it.neckar.lizergy.model.energy.PowerRating
import it.neckar.lizergy.model.financial.currency.MoneyFraction
import it.neckar.lizergy.model.financial.currency.cents100
import it.neckar.lizergy.model.financial.currency.euro100
import it.neckar.open.i18n.CurrentI18nConfiguration
import it.neckar.open.i18n.I18nConfiguration
import it.neckar.open.kotlin.lang.WhitespaceConfig
import it.neckar.open.unit.currency.EUR
import it.neckar.open.unit.other.pct
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

/**
 * Special class that represent a price per kWh.
 *
 * This class wraps a double value to be able to support the higher precision necessary.
 * It is *not* allowed to add/subtract these values
 */
@Serializable
@JvmInline
value class PricePerKWh(val price: @EUR MoneyFraction) {

  operator fun plus(other: PricePerKWh): PricePerKWh {
    return PricePerKWh(price + other.price)
  }

  operator fun minus(other: PricePerKWh): PricePerKWh {
    return PricePerKWh(price - other.price)
  }

  /**
   * Multiplies this value with a [PowerRating]
   */
  operator fun times(powerRating: PowerRating): PricePerKWh {
    return this * powerRating.kiloWattPeak
  }

  /**
   * Multiplies this value with an [AmountOfEnergy]
   */
  operator fun times(amount: AmountOfEnergy): MoneyFraction {
    return price.timesAndRound(amount.kWh)
  }

  operator fun times(factor: Double): PricePerKWh {
    return PricePerKWh(price.timesAndRound(factor))
  }

  fun increasingBy(increase: @pct Double): IncreasingPricePerKWh {
    return IncreasingPricePerKWh(this, increase)
  }

  override fun toString(): String {
    return "${price.format()} / kWh"
  }

  fun format(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    return "${price.format(i18nConfiguration, whitespaceConfig)}${whitespaceConfig.smallSpace}/${whitespaceConfig.smallSpace}kWh"
  }

  companion object {
    val zero: PricePerKWh = PricePerKWh(MoneyFraction.Zero)

    /**
     * Creates a price per kWh object from cents
     */
    fun cents(cents: @EUR Double): PricePerKWh {
      return PricePerKWh(cents.cents100)
    }

    fun euro(euro: @EUR Double): PricePerKWh {
      return PricePerKWh(euro.euro100)
    }
  }
}

